import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Rating,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FacebookIcon from "@mui/icons-material/Facebook";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ReviewsIcon from "@mui/icons-material/Reviews";
import InsertCommentIcon from "@mui/icons-material/InsertComment";
import EditIcon from "@mui/icons-material/Edit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";

type ReviewSource = "manual" | "google" | "facebook";

export type DesignerReview = {
  id: string;
  rating: number;
  text: string;
  author: string;
  location?: string;
  avatar?: string;
  source: ReviewSource;
  createdAt?: string;
};

type ReviewManagementProps = {
  reviews: DesignerReview[];
  onReviewsChange?: (reviews: DesignerReview[]) => void;
  cardsPerSlide?: number;
};

type ReviewFormState = {
  rating: number;
  text: string;
  author: string;
  location: string;
  avatar: string;
};

const SOURCE_CONFIG: Record<
  ReviewSource,
  {
    label: string;
    description: string;
    chipColor: "default" | "primary" | "success";
  }
> = {
  manual: {
    label: "Manual Input",
    description: "Type in a testimonial from an existing client.",
    chipColor: "default",
  },
  google: {
    label: "Google Reviews",
    description: "Link to a Google Business review and we’ll import it.",
    chipColor: "primary",
  },
  facebook: {
    label: "Facebook",
    description: "Facebook review import is not connected yet.",
    chipColor: "success",
  },
};

const MANUAL_REVIEW_MAX = 200;

const IMPORT_GOOGLE_REVIEW_QUERY = gql`
  query ImportGoogleReview($url: String!) {
    importGoogleReview(url: $url) {
      id
      rating
      text
      author
      location
      avatar
      source
      createdAt
    }
  }
`;

export const ReviewManagement = ({
  reviews,
  onReviewsChange,
  cardsPerSlide = 3,
}: ReviewManagementProps) => {
  const theme = useTheme();
  const isDownMd = useMediaQuery(theme.breakpoints.down("md"));
  const isDownSm = useMediaQuery(theme.breakpoints.down("sm"));

  const effectiveCardsPerSlide = useMemo(() => {
    if (isDownSm) return 1;
    if (isDownMd) return Math.min(2, cardsPerSlide);
    return cardsPerSlide;
  }, [cardsPerSlide, isDownMd, isDownSm]);

  const [localReviews, setLocalReviews] = useState<DesignerReview[]>(reviews);

  useEffect(() => {
    setLocalReviews(reviews);
  }, [reviews]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState<ReviewSource>("manual");
  const [formState, setFormState] = useState<ReviewFormState>({
    rating: 0,
    text: "",
    author: "",
    location: "",
    avatar: "",
  });
  const [manualCharCount, setManualCharCount] = useState(0);
  const [importUrl, setImportUrl] = useState("");
  const apolloClient = useApolloClient();
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const slides = Math.max(
    1,
    Math.ceil(localReviews.length / Math.max(1, effectiveCardsPerSlide))
  );

  useEffect(() => {
    if (currentSlide >= slides) {
      setCurrentSlide(Math.max(0, slides - 1));
    }
  }, [slides, currentSlide]);

  const displayedReviews = useMemo(() => {
    const start = currentSlide * effectiveCardsPerSlide;
    return localReviews.slice(start, start + effectiveCardsPerSlide);
  }, [currentSlide, effectiveCardsPerSlide, localReviews]);

  const upsertReview = useCallback(
    (review: DesignerReview, targetId?: string) => {
      setLocalReviews((prev) => {
        const idToUse = targetId || review.id;
        const exists = prev.some((r) => r.id === idToUse);
        const next = exists
          ? prev.map((r) => (r.id === idToUse ? { ...review, id: idToUse } : r))
          : [{ ...review, id: idToUse }, ...prev];
        onReviewsChange?.(next);
        return next;
      });
      setCurrentSlide(0);
      setEditingId(null);
    },
    [onReviewsChange]
  );

  const resetForm = useCallback(() => {
    setFormState({
      rating: 0,
      text: "",
      author: "",
      location: "",
      avatar: "",
    });
    setManualCharCount(0);
    setImportUrl("");
    setActiveSource("manual");
    setEditingId(null);
    setImportError(null);
    setIsImporting(false);
  }, []);

  const handleModalClose = useCallback(() => {
    setOpenModal(false);
    resetForm();
  }, [resetForm]);

  const handleDelete = useCallback(
    (id: string) => {
      setLocalReviews((prev) => {
        const next = prev.filter((r) => r.id !== id);
        onReviewsChange?.(next);
        return next;
      });
      // Reset pagination if we delete the last item on a slide
      setCurrentSlide((prev) => 0);
    },
    [onReviewsChange]
  );

  const confirmDelete = useCallback(() => {
    if (deleteTargetId) {
      handleDelete(deleteTargetId);
      setDeleteTargetId(null);
    }
  }, [deleteTargetId, handleDelete]);

  const cancelDelete = useCallback(() => setDeleteTargetId(null), []);

  const handleSubmitManual = useCallback(() => {
    const { rating, text, author, location, avatar } = formState;
    if (!rating || !text.trim() || !author.trim()) {
      return;
    }
    const existing = editingId ? localReviews.find((r) => r.id === editingId) : null;
    const newReview: DesignerReview = {
      id: editingId || `review-${Date.now()}`,
      rating,
      text: text.trim(),
      author: author.trim(),
      location: location.trim() || undefined,
      avatar:
        avatar.trim() ||
        `https://ui-avatars.com/api/?name=${encodeURIComponent(
          author.trim()
        )}&background=random`,
      source: existing?.source || "manual",
      createdAt: existing?.createdAt || new Date().toISOString(),
    };

    upsertReview(newReview, editingId || undefined);
    handleModalClose();
  }, [formState, upsertReview, handleModalClose, editingId, localReviews]);

  const handleImportReview = useCallback(async () => {
    if (!importUrl.trim()) return;
    if (activeSource === "facebook") {
      setImportError(
        "Facebook review import is not wired yet. Use Manual Input for Facebook testimonials."
      );
      return;
    }
    setIsImporting(true);
    setImportError(null);

    try {
      const { data } = await apolloClient.query({
        query: IMPORT_GOOGLE_REVIEW_QUERY,
        variables: { url: importUrl.trim() },
        fetchPolicy: "network-only",
      });

      const review = data?.importGoogleReview;
      if (!review) {
        throw new Error("No review data returned from Google.");
      }

      upsertReview(review);
      handleModalClose();
    } catch (error: unknown) {
      console.error("Failed to import review", error);
      const message =
        error instanceof Error
          ? error.message
          : "Unable to import the review.";
      setImportError(message);
    } finally {
      setIsImporting(false);
    }
  }, [activeSource, apolloClient, importUrl, handleModalClose, upsertReview]);

  const handleManualTextChange = useCallback((event: any) => {
    const next = event.target.value as string;
    if (next.length <= MANUAL_REVIEW_MAX) {
      setFormState((prev) => ({ ...prev, text: next }));
      setManualCharCount(next.length);
    }
  }, []);

  const renderSourceIcon = (source: ReviewSource, size: number = 20) => {
    switch (source) {
      case "facebook":
        return <FacebookIcon sx={{ fontSize: size, color: "#1877F2" }} />;
      case "google":
        return (
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              color: "primary.main",
              fontSize: size * 0.55,
            }}
          >
            G
          </Box>
        );
      default:
        return <InsertCommentIcon sx={{ fontSize: size, color: "text.secondary" }} />;
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Client Testimonials
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add praise from past projects to build homeowner trust.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setOpenModal(true)}
          startIcon={<ReviewsIcon />}
          sx={{
            textTransform: "none",
            backgroundColor: "black",
            "&:hover": {
              backgroundColor: "#1c1c1c",
            },
          }}
        >
          Add Review
        </Button>
      </Stack>

      <Box>
        {localReviews.length === 0 ? (
          <Card
            variant="outlined"
            sx={{
              borderRadius: 3,
              py: 6,
              px: 4,
              textAlign: "center",
            }}
          >
            <FormatQuoteIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            <Typography variant="h6" mt={1}>
              No reviews yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1.5}>
              Collect testimonials from homeowners you have worked with to show your expertise.
            </Typography>
            <Button
              variant="contained"
              sx={{ mt: 3, textTransform: "none", backgroundColor: "black" }}
              onClick={() => setOpenModal(true)}
            >
              Add your first review
            </Button>
          </Card>
        ) : (
          <Box>
            <Grid container spacing={3}>
              {displayedReviews.map((review) => (
                <Grid item xs={12} md={12 / effectiveCardsPerSlide} key={review.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      border: "1px solid",
                      borderColor: "grey.200",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        rowGap={1}
                        mb={2}
                      >
                        <Rating
                          value={review.rating}
                          precision={0.5}
                          readOnly
                          size="small"
                        />
                        <Chip
                          size="small"
                          label={SOURCE_CONFIG[review.source].label}
                          color={SOURCE_CONFIG[review.source].chipColor}
                          variant={review.source === "manual" ? "outlined" : "filled"}
                        />
                        {renderSourceIcon(review.source, 18)}
                        <Box flexGrow={1} />
                        <Stack direction="row" spacing={0.5} sx={{ ml: "auto" }}>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteTargetId(review.id)}
                            aria-label="Delete testimonial"
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setFormState({
                                rating: review.rating,
                                text: review.text,
                                author: review.author,
                                location: review.location || "",
                                avatar: review.avatar || "",
                              });
                              setManualCharCount(review.text?.length || 0);
                              setActiveSource(review.source);
                              setEditingId(review.id);
                              setOpenModal(true);
                            }}
                            aria-label="Edit testimonial"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic", mb: 3 }}
                      >
                        “{review.text}”
                      </Typography>
                    </CardContent>
                    <Divider />
                    <Box px={3} py={2} display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={review.avatar}
                        alt={review.author}
                        sx={{ width: 52, height: 52 }}
                      >
                        {review.author.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {review.author}
                        </Typography>
                        {review.location && (
                          <Typography variant="caption" color="text.secondary">
                            {review.location}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {slides > 1 && (
              <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
                <IconButton
                  onClick={() =>
                    setCurrentSlide((prev) =>
                      (prev - 1 + slides) % slides
                    )
                  }
                  sx={{ border: "1px solid", borderColor: "grey.300" }}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Stack direction="row" spacing={1} alignItems="center">
                  {Array.from({ length: slides }).map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor:
                          currentSlide === index ? "primary.main" : "grey.300",
                        transition: "background-color 0.2s ease",
                        cursor: "pointer",
                      }}
                    />
                  ))}
                </Stack>
                <IconButton
                  onClick={() => setCurrentSlide((prev) => (prev + 1) % slides)}
                  sx={{ border: "1px solid", borderColor: "grey.300" }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Stack>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={openModal}
        onClose={handleModalClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 2,
          }}
        >
          <Typography variant="h5" fontWeight={600}>
            Add a Review
          </Typography>
          <IconButton onClick={handleModalClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Review Source
          </Typography>
          <Grid container spacing={1.5} mb={3}>
            {(Object.keys(SOURCE_CONFIG) as ReviewSource[]).map((source) => {
              const active = activeSource === source;
              return (
                <Grid item xs={4} key={source}>
                  <Button
                    fullWidth
                    variant={active ? "outlined" : "outlined"}
                    onClick={() => setActiveSource(source)}
                    sx={{
                      borderWidth: 2,
                      textTransform: "none",
                      py: 1.5,
                      borderColor: active ? "black" : "grey.300",
                      bgcolor: active ? "grey.50" : "background.paper",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      "&:hover": { borderColor: active ? "black" : "grey.400" },
                    }}
                  >
                    {renderSourceIcon(source, 22)}
                    <Typography variant="body2" fontWeight={600}>
                      {SOURCE_CONFIG[source].label}
                    </Typography>
                  </Button>
                </Grid>
              );
            })}
          </Grid>

          {activeSource === "manual" ? (
            <Stack spacing={3}>
              <Box textAlign="center">
                <Typography variant="subtitle1" fontWeight={600} mb={1}>
                  Overall Rating
                </Typography>
                <Rating
                  value={formState.rating}
                  precision={0.5}
                  onChange={(_, value) =>
                    setFormState((prev) => ({
                      ...prev,
                      rating: value ?? 0,
                    }))
                  }
                  sx={{ fontSize: 40 }}
                />
                <Typography variant="body2" color="text.secondary" mt={1.5}>
                  Help homeowners by sharing others’ experience working with you.
                </Typography>
              </Box>

              <Box>
                <TextField
                  multiline
                  minRows={4}
                  label="Review"
                  value={formState.text}
                  onChange={handleManualTextChange}
                  placeholder="Share the feedback exactly as written by your client."
                  fullWidth
                  inputProps={{ maxLength: MANUAL_REVIEW_MAX }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "right", mt: 0.5 }}
                >
                  {manualCharCount}/{MANUAL_REVIEW_MAX}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ width: "100%" }}
              >
                <TextField
                  label="Client Name"
                  value={formState.author}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      author: event.target.value,
                    }))
                  }
                  fullWidth
                  placeholder="Emily Johnson"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Location"
                  value={formState.location}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      location: event.target.value,
                    }))
                  }
                  fullWidth
                  placeholder="City & State"
                  sx={{ flex: 1 }}
                />
              </Stack>

              <TextField
                label="Avatar URL (optional)"
                value={formState.avatar}
                onChange={(event) =>
                  setFormState((prev) => ({
                    ...prev,
                    avatar: event.target.value,
                  }))
                }
                fullWidth
                placeholder="https://example.com/avatar.jpg"
              />

              <Typography variant="caption" color="text.secondary">
                By submitting this review, you agree that you have obtained permission
                from your client to share this testimonial publicly.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Box>
                <TextField
                  label={`${SOURCE_CONFIG[activeSource].label} URL`}
                  value={importUrl}
                  onChange={(event) => setImportUrl(event.target.value)}
                  placeholder={`Paste the ${SOURCE_CONFIG[activeSource].label.toLowerCase()} link here`}
                  fullWidth
                />
                <Typography variant="caption" color="text.secondary" mt={1.5}>
                  {activeSource === "google"
                    ? "Google short share links sometimes resolve only to a business profile. Full Google Maps review links import more reliably."
                    : "Facebook review import is not available yet. Use Manual Input for Facebook testimonials."}
                </Typography>
                {importError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 1 }}
                  >
                    {importError}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button
            variant="outlined"
            onClick={handleModalClose}
            sx={{ textTransform: "none", borderColor: "grey.300" }}
          >
            Cancel
          </Button>
          {activeSource === "manual" ? (
            <Button
              variant="contained"
              onClick={handleSubmitManual}
              disabled={
                !formState.rating || !formState.text.trim() || !formState.author.trim()
              }
              sx={{
                backgroundColor: "black",
                textTransform: "none",
                "&:hover": { backgroundColor: "#1c1c1c" },
              }}
            >
              Submit Review
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleImportReview}
              disabled={!importUrl.trim() || isImporting || activeSource === "facebook"}
              sx={{
                backgroundColor: "black",
                textTransform: "none",
                "&:hover": { backgroundColor: "#1c1c1c" },
              }}
            >
              {activeSource === "facebook"
                ? "Coming Soon"
                : isImporting
                  ? "Importing..."
                  : "Import Review"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTargetId)}
        onClose={cancelDelete}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Remove testimonial?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently delete the review. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="inherit">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewManagement;
